import React, {useState, useEffect} from "react";
import {NavLink} from "react-router-dom";
import {useAuth} from "../auth/AuthContext";
import ToolsDropdown from "./ToolsDropdown";
import UserDropdown from "./UserDropdown";
import {signOut} from "firebase/auth";
import {auth} from "../firebase";
import NotificationPopup from "../components/NotificationPopup";
import {useTexts} from "../hooks/useTexts";

const Navbar: React.FC = () => {
    const {getText} = useTexts();
    const {user, isAdmin} = useAuth();
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [popupMessage, setPopupMessage] = useState("");
    const [popupType, setPopupType] = useState<'info' | 'success' | 'warning' | 'error'>('info');

    const showNotification = (message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info') => {
        setPopupMessage(message);
        setPopupType(type);
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
    };

    const navLinks = [
        {to: "/", icon: "🏠", label: getText("nav_home", "Home")},
        {to: "/courses", icon: "📚", label: getText("nav_courses", "Kurse")},
        {to: "/worksheets", icon: "📄", label: getText("nav_worksheets", "Arbeitsblätter")},
    ];

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <>
            <header
                className={`fixed top-0 left-0 right-0 z-50 h-16 w-full backdrop-blur-xl transition-all duration-300 ${
                    scrolled
                        ? 'bg-white/95 shadow-md border-b border-gray-200'
                        : 'bg-white/90 shadow-sm'
                }`}>
                <div className="h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
                    <NavLink to="/" className="flex items-center gap-3 no-underline" onClick={() => setMenuOpen(false)}>
                        <img
                            src="/assets/logo.png"
                            alt="Logo"
                            className="w-9 h-9 rounded-xl object-cover"
                        />
                        <div className="flex flex-col">
                            <span
                                className="text-xl font-black bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                                {getText("app_name", "IslamHub")}
                            </span>
                            <span className="text-[0.6rem] font-bold text-[#D4AF37] tracking-wider">BETA</span>
                        </div>
                    </NavLink>

                    <nav className="hidden lg:flex items-center gap-1">
                        {navLinks.map((link) => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                end
                                className={({isActive}) =>
                                    `flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-200 text-sm font-medium ${
                                        isActive
                                            ? 'bg-green-500/15 text-green-600'
                                            : 'text-gray-600 hover:text-gray-800 hover:bg-black/5'
                                    }`
                                }
                            >
                                <span className="text-lg">{link.icon}</span>
                                <span>{link.label}</span>
                            </NavLink>
                        ))}
                        <ToolsDropdown/>
                        {isAdmin && (
                            <NavLink
                                to="/admin/texts"
                                className={({isActive}) =>
                                    `flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-200 text-sm font-medium ${
                                        isActive
                                            ? 'bg-green-500/15 text-green-600'
                                            : 'text-gray-600 hover:text-gray-800 hover:bg-black/5'
                                    }`
                                }
                            >
                                <span className="text-lg">✏️</span>
                                <span>{getText("nav_text_editor", "Text-Editor")}</span>
                            </NavLink>
                        )}
                    </nav>

                    <div className="flex items-center gap-3">
                        {user ? (
                            <>
                                <button
                                    className="hidden lg:flex relative w-9 h-9 rounded-xl bg-black/5 border border-gray-200 text-gray-600 hover:bg-black/10 hover:border-gray-300 transition-all duration-200 items-center justify-center">
                                    <svg width="18" height="18" viewBox="0 0 24 24">
                                        <path
                                            d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                                            stroke="currentColor" strokeWidth="2"/>
                                    </svg>
                                </button>
                                <button
                                    className="hidden lg:flex relative w-9 h-9 rounded-xl bg-black/5 border border-gray-200 text-gray-600 hover:bg-black/10 hover:border-gray-300 transition-all duration-200 items-center justify-center"
                                    onClick={() => showNotification(getText("nav_notification_info", "Benachrichtigungsfunktion in Entwicklung"), "info")}
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24">
                                        <path
                                            d="M15 17H20L18.5951 15.5951C18.2141 15.2141 18 14.6973 18 14.1585V11C18 8.38757 16.3304 6.16509 14 5.34142V5C14 3.89543 13.1046 3 12 3C10.8954 3 10 3.89543 10 5V5.34142C7.66962 6.16509 6 8.38757 6 11V14.1585C6 14.6973 5.78595 15.2141 5.40493 15.5951L4 17H9M15 17V18C15 19.6569 13.6569 21 12 21C10.3431 21 9 19.6569 9 18V17M15 17H9"
                                            stroke="currentColor" strokeWidth="2"/>
                                    </svg>
                                    <span
                                        className="absolute -top-1 -right-1 bg-green-500 text-white text-xs font-bold w-4 h-4 rounded-full flex items-center justify-center">1</span>
                                </button>
                                <UserDropdown user={user}/>
                            </>
                        ) : (
                            <div className="hidden lg:flex items-center gap-2">
                                <NavLink to="/login"
                                         className="px-4 py-2 rounded-xl bg-black/5 border border-gray-200 text-gray-700 hover:bg-black/10 hover:border-gray-300 transition-all duration-200 text-sm font-medium">
                                    {getText("nav_login", "Anmelden")}
                                </NavLink>
                                <NavLink to="/register"
                                         className="px-5 py-2 rounded-xl bg-gradient-to-br from-green-500 to-blue-500 text-white hover:shadow-lg hover:shadow-green-500/30 transition-all duration-200 text-sm font-semibold">
                                    {getText("nav_register", "Starten")}
                                </NavLink>
                            </div>
                        )}

                        <button
                            className={`lg:hidden flex flex-col gap-1.5 p-2 z-50 ${menuOpen ? 'open' : ''}`}
                            onClick={() => setMenuOpen(!menuOpen)}
                        >
                            <span
                                className={`block w-6 h-0.5 bg-gray-800 transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`}/>
                            <span
                                className={`block w-6 h-0.5 bg-gray-800 transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`}/>
                            <span
                                className={`block w-6 h-0.5 bg-gray-800 transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`}/>
                        </button>
                    </div>
                </div>
            </header>

            {menuOpen && (
                <div className="lg:hidden fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
                     onClick={() => setMenuOpen(false)}/>
            )}

            <div
                className={`lg:hidden fixed top-0 right-0 h-full w-80 bg-white border-l border-gray-200 shadow-xl transition-transform duration-300 z-50 ${
                    menuOpen ? 'translate-x-0' : 'translate-x-full'
                }`}>
                <div className="p-5 border-b border-gray-200 flex items-start justify-between">
                    <div className="flex items-center gap-3">
                        {user ? (
                            <>
                                <div
                                    className="w-10 h-10 bg-gradient-to-br from-green-500 to-blue-500 rounded-xl flex items-center justify-center font-semibold text-lg text-white">
                                    {user.email?.[0]?.toUpperCase() || "U"}
                                </div>
                                <div>
                                    <div className="font-semibold text-gray-800 text-sm">{user.email}</div>
                                    <div
                                        className="text-xs text-gray-500">{getText("nav_logged_in", "Eingeloggt")}</div>
                                </div>
                            </>
                        ) : (
                            <div>
                                <p className="font-semibold text-gray-800 mb-2 text-sm">{getText("nav_welcome", "Willkommen bei IslamHub")}</p>
                                <div className="flex flex-col gap-2">
                                    <NavLink to="/login"
                                             className="px-3 py-2 rounded-xl bg-black/5 border border-gray-200 text-gray-700 hover:bg-black/10 transition-all duration-200 font-medium text-center text-sm"
                                             onClick={() => setMenuOpen(false)}>
                                        {getText("nav_login", "Anmelden")}
                                    </NavLink>
                                    <NavLink to="/register"
                                             className="px-3 py-2 rounded-xl bg-gradient-to-br from-green-500 to-blue-500 text-white hover:shadow-lg hover:shadow-green-500/30 transition-all duration-200 font-semibold text-center text-sm"
                                             onClick={() => setMenuOpen(false)}>
                                        {getText("nav_register", "Registrieren")}
                                    </NavLink>
                                </div>
                            </div>
                        )}
                    </div>
                    <button className="text-xl text-gray-500 hover:text-gray-800" onClick={() => setMenuOpen(false)}>✕
                    </button>
                </div>

                <div className="overflow-y-auto h-[calc(100vh-120px)] py-3 custom-scrollbar">
                    {navLinks.map((link) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            className={({isActive}) =>
                                `flex items-center gap-3 px-5 py-3 transition-all duration-200 ${
                                    isActive
                                        ? 'bg-green-500/15 text-green-600'
                                        : 'text-gray-600 hover:text-gray-800 hover:bg-black/5'
                                }`
                            }
                            onClick={() => setMenuOpen(false)}
                        >
                            <span className="text-lg">{link.icon}</span>
                            <span className="font-medium text-sm">{link.label}</span>
                        </NavLink>
                    ))}

                    {isAdmin && (
                        <NavLink
                            to="/admin/texts"
                            className={({isActive}) =>
                                `flex items-center gap-3 px-5 py-3 transition-all duration-200 ${
                                    isActive
                                        ? 'bg-green-500/15 text-green-600'
                                        : 'text-gray-600 hover:text-gray-800 hover:bg-black/5'
                                }`
                            }
                            onClick={() => setMenuOpen(false)}
                        >
                            <span className="text-lg">✏️</span>
                            <span className="font-medium text-sm">{getText("nav_text_editor", "Text-Editor")}</span>
                        </NavLink>
                    )}

                    <div className="h-px bg-gray-200 my-3 mx-5"/>
                    <div
                        className="px-5 py-1 font-semibold text-gray-800 mb-2 text-sm">{getText("nav_tools", "🛠️ Tools")}</div>

                    <NavLink to="/chatbot"
                             className={({isActive}) => `flex items-center gap-3 px-5 py-3 transition-all duration-200 ${isActive ? 'bg-green-500/15 text-green-600' : 'text-gray-600 hover:text-gray-800 hover:bg-black/5'}`}
                             onClick={() => setMenuOpen(false)}>
                        <span className="text-lg">🤖</span><span
                        className="font-medium text-sm">{getText("nav_chatbot", "AI Chatbot")}</span>
                    </NavLink>
                    <NavLink to="/tasks"
                             className={({isActive}) => `flex items-center gap-3 px-5 py-3 transition-all duration-200 ${isActive ? 'bg-green-500/15 text-green-600' : 'text-gray-600 hover:text-gray-800 hover:bg-black/5'}`}
                             onClick={() => setMenuOpen(false)}>
                        <span className="text-lg">📝</span><span
                        className="font-medium text-sm">{getText("nav_tasks", "Aufgaben Creator")}</span>
                    </NavLink>
                    <NavLink to="/youtube-tasks"
                             className={({isActive}) => `flex items-center gap-3 px-5 py-3 transition-all duration-200 ${isActive ? 'bg-green-500/15 text-green-600' : 'text-gray-600 hover:text-gray-800 hover:bg-black/5'}`}
                             onClick={() => setMenuOpen(false)}>
                        <span className="text-lg">🎬</span><span
                        className="font-medium text-sm">{getText("nav_youtube", "YouTube Aufgaben")}</span>
                    </NavLink>
                    <NavLink to="/kahoot-creator"
                             className={({isActive}) => `flex items-center gap-3 px-5 py-3 transition-all duration-200 ${isActive ? 'bg-green-500/15 text-green-600' : 'text-gray-600 hover:text-gray-800 hover:bg-black/5'}`}
                             onClick={() => setMenuOpen(false)}>
                        <span className="text-lg">🎮</span><span
                        className="font-medium text-sm">{getText("nav_kahoot", "Kahoot Generator")}</span>
                    </NavLink>

                    {user && (
                        <>
                            <div className="h-px bg-gray-200 my-3 mx-5"/>
                            <NavLink to="/account"
                                     className={({isActive}) => `flex items-center gap-3 px-5 py-3 transition-all duration-200 ${isActive ? 'bg-green-500/15 text-green-600' : 'text-gray-600 hover:text-gray-800 hover:bg-black/5'}`}
                                     onClick={() => setMenuOpen(false)}>
                                <span className="text-lg">👤</span><span
                                className="font-medium text-sm">{getText("nav_account", "Mein Account")}</span>
                            </NavLink>
                            <button
                                className="flex items-center gap-3 px-5 py-3 w-full text-red-500 hover:text-red-600 hover:bg-red-500/10 transition-all duration-200 text-left"
                                onClick={() => {
                                    signOut(auth);
                                    setMenuOpen(false);
                                    showNotification(getText("nav_logout_success", "Erfolgreich abgemeldet"), "success");
                                }}>
                                <span className="text-lg">🚪</span><span
                                className="font-medium text-sm">{getText("nav_logout", "Logout")}</span>
                            </button>
                        </>
                    )}
                </div>
            </div>

            <NotificationPopup showPopup={showPopup} popupMessage={popupMessage} type={popupType} position="top-right"/>
        </>
    );
};

export default Navbar;